import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { map } from 'rxjs/operators';

// Juice billed per cup, entered by hand once a month per system.
//
// Juice comes from BIB (bag-in-box) dispensers that report nothing — unlike the
// coffee machines (Vendon, see vendonCoffeeBillable.js) — so the count is manual.
// This replaces the accountant's "Juice Bib kalkulator" + "Fakturagrunnlag
// Drikke" sheets: bibs used × cups per bib × price per cup (12 kr ex. VAT).
// Cups per bib are the sheet's own figures (5 l concentrate, 0,2 l cups).
export const JUICE_TYPES: { key: string, label: string, cupsPerBib: number }[] = [
  { key: 'appelsin', label: 'Appelsinjuice Rauch', cupsPerBib: 120 },
  { key: 'eple', label: 'Eplejuice Rauch', cupsPerBib: 145 },
  { key: 'iste_fersken', label: 'Iste fersken Rauch', cupsPerBib: 250 },
  { key: 'cups', label: 'Cups counted directly', cupsPerBib: 0 }
];
export const JUICE_DEFAULT_PRICE_EX_VAT = 12;

export interface JuiceEntry {
  dbid?: string;
  month: string;               // 'YYYY-MM' the juice is billed for
  systemId: string;
  juiceType: string;           // JUICE_TYPES key
  bibs: number | null;         // null when cups were counted directly
  cups: number;
  pricePerCupExVat: number;
  note: string;
  createdBy?: string;
}

export interface JuiceBillable {
  total: number;
  lines: { systemId: string, systemName: string, cups: number, pricePerCupExVat: number, amountExVat: number }[];
}

@Injectable({ providedIn: 'root' })
export class JuiceService {

  constructor(private afs: AngularFirestore) { }

  listForMonth$(month: string) {
    return this.afs.collection('juice-entries', ref => ref.where('month', '==', month)).snapshotChanges().pipe(map(docs =>
      docs.map(d => ({ dbid: d.payload.doc.id, ...(d.payload.doc.data() as any) } as JuiceEntry))
    ));
  }

  add(entry: JuiceEntry) {
    const { dbid, ...data } = entry as any;
    return this.afs.collection('juice-entries').add({ ...data, createdAt: Date.now() });
  }

  remove(dbid: string) {
    return this.afs.collection('juice-entries').doc(dbid).delete();
  }

  // systemId -> kundekortId for systems whose juice has its own payer
  // (system_info.juiceKundekortId) — e.g. Propcap, where purchases are billed to
  // Phoenix Contact but the juice to the landlord. Keyed by doc id and alias.
  async getExplicitPayers(): Promise<{ [systemId: string]: string }> {
    const snap = await this.afs.collection('system_info').ref.get();
    const out: { [systemId: string]: string } = {};
    snap.docs.forEach(d => {
      const s = d.data() as any;
      if (!s.juiceKundekortId) { return; }
      out[d.id] = s.juiceKundekortId;
      if (s.systemId) { out[String(s.systemId)] = s.juiceKundekortId; }
    });
    return out;
  }

  // Per-customer juice for a month. The payer is the system's OWNER, resolved
  // through system-kunder exactly like AvtalelinjeService — an entry on a system
  // with no owner throws, so it can never silently drop off every invoice.
  // Entries for the same system and price are summed into one invoice line.
  async getBillableByCustomer(month: string): Promise<{ [kundekortId: string]: JuiceBillable }> {
    const [entriesSnap, sysSnap, linksSnap, kunderSnap] = await Promise.all([
      // afs.firestore, not afs.collection(path, q => ...).ref: `.ref` is the bare
      // collection and silently drops the where(), which read every month's rows.
      this.afs.firestore.collection('juice-entries').where('month', '==', month).get(),
      this.afs.collection('system_info').ref.get(),
      this.afs.collection('system-kunder').ref.get(),
      this.afs.collection('kunder').ref.get()
    ]);
    const liveCustomer = new Set(kunderSnap.docs.map(d => d.id));
    const linkBySystem: { [systemId: string]: string } = {};
    linksSnap.docs.forEach(d => {
      const l = d.data() as any;
      if (l.systemId && l.kundekortId && liveCustomer.has(l.kundekortId)) { linkBySystem[String(l.systemId)] = l.kundekortId; }
    });
    const sysName: { [id: string]: string } = {};
    const aliasToDocId: { [id: string]: string } = {};
    sysSnap.docs.forEach(d => {
      const s = d.data() as any;
      sysName[d.id] = s.name || d.id;
      if (s.systemId) { sysName[String(s.systemId)] = s.name || d.id; aliasToDocId[String(s.systemId)] = d.id; }
    });
    const ownerOf = (systemId: string) => linkBySystem[systemId] || linkBySystem[aliasToDocId[systemId]] || null;

    const out: { [kundekortId: string]: JuiceBillable } = {};
    const orphans: string[] = [];
    entriesSnap.docs.forEach(d => {
      const e = d.data() as any;
      const cups = Number(e.cups) || 0;
      const price = Number(e.pricePerCupExVat) || 0;
      if (!(cups > 0) || !(price > 0)) { return; }
      const owner = ownerOf(String(e.systemId));
      if (!owner) { orphans.push(sysName[String(e.systemId)] || String(e.systemId)); return; }
      const b = out[owner] || (out[owner] = { total: 0, lines: [] });
      let line = b.lines.find(l => l.systemId === String(e.systemId) && l.pricePerCupExVat === price);
      if (!line) {
        line = { systemId: String(e.systemId), systemName: sysName[String(e.systemId)] || String(e.systemId), cups: 0, pricePerCupExVat: price, amountExVat: 0 };
        b.lines.push(line);
      }
      line.cups += cups;
      line.amountExVat = +(line.cups * price).toFixed(2);
    });
    if (orphans.length) {
      throw new Error(`Juice entered for system(s) with no customer linked: ${orphans.join(', ')}. Link the system to a kundekort before invoicing.`);
    }
    Object.values(out).forEach(b => { b.total = +b.lines.reduce((n, l) => n + l.amountExVat, 0).toFixed(2); });
    return out;
  }
}
